import type {
  TransformedCheckoutDetail,
  TransformedVehicleOption,
  TransformedFeature,
  PricingSummaryItem,
} from '../inventory/transformers.checkout';

/*
|--------------------------------------------------------------------------
| QUOTE VEHICLE DETAILS TYPES (from API response)
|--------------------------------------------------------------------------
*/

type QuoteVehicleOption = {
  label: string;
  name: string;
  code: string;
  type: string;
  value: number;
  description?: string;
};

type QuoteFeature = {
  name: string;
  code: string;
  type: string;
  value: number;
  description?: string;
};

type QuotePricing = {
  basePrice: number;
  addOns: number;
  subtotal: number;
  fees: { label: string; value: number }[];
  credits: { label: string; value: number }[];
  deposit: number;
  total: number;
};

type QuoteVehicleDetails = {
  vin: string;
  shortcode: string;
  vehicleImage: string;
  model: string;
  modelYear: string;
  trim: string;
  type: string;
  availability: string;
  currencyCode: string;
  vehicleOptions: QuoteVehicleOption[];
  features: QuoteFeature[];
  pricing: QuotePricing;
};

export type QuoteDetailsResponse = {
  quoteIdentifier: string;
  sfdcQuoteIdentifier: string | null;
  prospectIdentifier: string;
  businessPartnerUserIdentifier: string;
  businessPartnerOrganizationIdentifier: string;
  prospectType: string;
  prospectDetails: Record<string, unknown>;
  companyName: string;
  companyTaxIdentification: string;
  invoiceAddressLine1: string;
  invoiceAddressLine2: string;
  invoiceCity: string;
  invoicePostalCode: string;
  deliveryTimeline: string;
  isDeliveryThisQuarter: boolean;
  model: string;
  trim: string;
  vin: string;
  vehicleCondition: string;
  isBuildToOrder: boolean;
  configurationShortCode: string;
  isWinterWheelsRequired: boolean;
  winterWheelsQuantity: number;
  financingType: string;
  preferredPaymentMethod: string;
  quotationDate: string;
  sharedDate: string;
  expirationDate: string;
  acceptedDate: string;
  quoteCurrency: string;
  totalAmount: number | null;
  isTermsAndConditionsAccepted: boolean;
  vehicleDetails: QuoteVehicleDetails;
  vehiclePricingDetails: {
    items: { label: string; amount: number }[];
  };
  taxesAndFeesDetails: Record<string, unknown>;
  discountsAndIncentivesDetails: {
    label: string;
    amount: number;
  };
  financingDetails: Record<string, unknown>;
  vehicleHoldStartDatetime: string;
  vehicleHoldExpiryDatetime: string;
  docusignEmailAddress: string;
  docusignStatus: string;
  lastSyncedToSalesforce: string | null;
  timezone: string;
  isActive: boolean;
  isDeleted: boolean;
  createdDatetime: string;
  updatedDatetime: string;
  createdBy: string;
  updatedBy: string | null;
  currentStatus: string;
  statusHistory: unknown;
};

/*
|--------------------------------------------------------------------------
| HELPER FUNCTIONS
|--------------------------------------------------------------------------
*/

/**
 * Maps API prospect type values to form dropdown values
 */
const mapProspectType = (value: string | undefined): string => {
  if (!value) return '';
  const normalized = value.toLowerCase();
  const mapping: Record<string, string> = {
    private: 'Private',
    business: 'Business',
  };
  return mapping[normalized] ?? normalized;
};

/**
 * Maps API payment method values to form dropdown values
 */
const mapPaymentMethod = (value: string | undefined): string => {
  if (!value) return '';
  const normalized = value.toLowerCase().replace(/[\s-]/g, '_');
  const mapping: Record<string, string> = {
    cash: 'Cash',
    lfs: 'LFS Loan',
    '3rd_party_finance': '3rd Party Financing',
    third_party_finance: '3rd Party Financing',
  };
  return mapping[normalized] ?? normalized;
};

/**
 * Transforms quote vehicle options to the standard format
 * Excludes 'model' label as it's not a user-selectable option
 */
const transformVehicleOptions = (
  vehicleOptions: QuoteVehicleOption[] | undefined
): TransformedVehicleOption[] => {
  if (!vehicleOptions) return [];

  return vehicleOptions
    .filter((option) => option.label !== 'model')
    .map((option) => ({
      label: option.label || '',
      name: option.name || '',
      code: option.code || '',
      type: option.type || '',
      value: option.value ?? 0,
      description: option.description,
    }));
};

/**
 * Transforms quote features to the standard format
 */
const transformFeatures = (features: QuoteFeature[] | undefined): TransformedFeature[] => {
  if (!features) return [];

  return features.map((feature) => ({
    name: feature.name || '',
    code: feature.code || '',
    type: feature.type || '',
    value: feature.value ?? 0,
    description: feature.description,
  }));
};

/**
 * Extracts the base price (variant value) from vehicle options
 */
const extractBasePrice = (vehicleOptions: QuoteVehicleOption[] | undefined): number => {
  const variantItem = vehicleOptions?.find((item) => item.label === 'variant');
  return variantItem?.value ?? 0;
};

/*
|--------------------------------------------------------------------------
| TRANSFORMER FUNCTION
|--------------------------------------------------------------------------
*/

/**
 * Transforms the quote details API response to a TransformedCheckoutDetail format
 * This allows the quote data to be used with the same UI components as checkout data
 * @param response - The raw quote details API response
 * @returns Transformed checkout detail object
 * @example
 * ```typescript
 * const transformed = transformQuoteToCheckoutDetail(quoteResponse);
 * console.log(transformed.model); // 'air'
 * console.log(transformed.vehicleOptions[0].name); // 'Touring'
 * ```
 */
export const transformQuoteToCheckoutDetail = (
  response: QuoteDetailsResponse
): TransformedCheckoutDetail => {
  const { vehicleDetails } = response;
  const pricing = vehicleDetails?.pricing;
  // console.log('vehicleDetails111----', vehicleDetails);
  // Extract pricing values
  const basePrice = extractBasePrice(vehicleDetails?.vehicleOptions);
  const addOns = pricing?.addOns ?? 0;
  const subtotal = pricing?.subtotal ?? 0;
  const deposit = pricing?.deposit ?? 0;
  const total = pricing?.total ?? 0;

  // Extract fees and credits
  const fees: PricingSummaryItem[] =
    pricing?.fees?.map((fee) => ({
      label: fee.label,
      value: fee.value,
    })) ?? [];

  const credits: PricingSummaryItem[] =
    pricing?.credits?.map((credit) => ({
      label: credit.label,
      value: credit.value,
    })) ?? [];

  return {
    // Vehicle identification
    vin: vehicleDetails?.vin || '',
    shortcode: vehicleDetails?.shortcode || '',
    vehicleImage: vehicleDetails?.vehicleImage || '',

    // Vehicle details
    model: vehicleDetails?.model || '',
    modelYear: vehicleDetails?.modelYear || '',
    trim: vehicleDetails?.trim || '',
    type: vehicleDetails?.type || '',
    availability: vehicleDetails?.availability || '',

    // Currency
    currencyCode: vehicleDetails?.currencyCode || 'USD',

    // Vehicle options and features
    vehicleOptions: transformVehicleOptions(vehicleDetails?.vehicleOptions),
    features: transformFeatures(vehicleDetails?.features),

    // Pricing breakdown
    pricing: {
      basePrice,
      addOns,
      subtotal,
      fees,
      credits,
      deposit,
      total,
    },
    rightSideSummary: {
      prospectType: mapProspectType(response.prospectType),
      preferredPaymentMethod: mapPaymentMethod(
        response.preferredPaymentMethod || response['paymentMethod']
      ),
      deliveryTimeline: response.deliveryTimeline,
      companyName: response.companyName,
      companyTaxIdentification: response.companyTaxIdentification,
      invoiceAddressLine1: response.invoiceAddressLine1,
      invoiceCity: response.invoiceCity,
      invoicePostalCode: response.invoicePostalCode,
    },
    docusignStatus: response.docusignStatus || '',
    currentStatus: response.currentStatus || '',
  };
};
